import type { Ref } from 'react';

import type { BlockedDevices, TeleprompterSpeed } from './videoRecorder.utils';
import { cn } from '../cn';
import Button from '../Input2/Button';
import ButtonGroup from '../Input2/ButtonGroup';
import { Arrows, General, Images, Media, Shapes } from '../StrokeIcons';
import { formatClock } from './videoRecorder.utils';

export type VideoRecorderPhase =
  | 'idle' // camera not opened yet
  | 'starting' // waiting on the camera permission prompt
  | 'ready' // live preview, not recording
  | 'countdown'
  | 'recording'
  | 'paused'
  | 'preview' // recorded, playing back before sending
  | 'sending'
  | 'denied' // camera/mic permission refused
  | 'no_camera' // no usable camera on this device
  | 'in_use' // the camera exists but another app is holding it
  | 'error';

export type VideoRecorderPureProps = {
  phase: VideoRecorderPhase;
  /** What the rep reads — scrolls on the teleprompter while recording. */
  script: string;
  speed: TeleprompterSpeed;
  mirrored: boolean;
  /** Seconds left in the pre-roll countdown. */
  countdown: number;
  elapsedMs: number;
  maxMs: number;
  /** 0 → 1: how far the teleprompter has scrolled (driven by recording time, so pausing pauses it). */
  teleprompterProgress: number;
  /** Object URL of the finished recording, for the preview player. */
  previewUrl: string | null;
  errorMessage?: string | null;
  /** When denied: which device the browser blocked (null = unknown — name both). */
  blockedDevices?: BlockedDevices | null;
  /** Attach the live camera stream to the preview <video>. */
  liveVideoRef?: Ref<HTMLVideoElement>;
  onOpenCamera: () => void;
  onRecord: () => void;
  onStop: () => void;
  onPauseToggle: () => void;
  onRetake: () => void;
  onSend: () => void;
  onCancel: () => void;
  onSpeedChange: (speed: TeleprompterSpeed) => void;
  onMirrorToggle: () => void;
  className?: string;
};

const SPEEDS: { value: TeleprompterSpeed; label: string }[] = [
  { value: 'slow', label: 'Slow' },
  { value: 'normal', label: 'Normal' },
  { value: 'fast', label: 'Fast' },
];

/**
 * The video-note recorder, data-free: a 9:16 camera frame with the script scrolling on a teleprompter
 * band near the top (so the rep's eyes stay close to the lens), a countdown, a timer against the cap,
 * and a preview to watch before sending. All state comes in as props — see `VideoRecorder` for the
 * MediaRecorder / camera wiring.
 */
export function VideoRecorderPure({
  phase,
  script,
  speed,
  mirrored,
  countdown,
  elapsedMs,
  maxMs,
  teleprompterProgress,
  previewUrl,
  errorMessage,
  blockedDevices = null,
  liveVideoRef,
  onOpenCamera,
  onRecord,
  onStop,
  onPauseToggle,
  onRetake,
  onSend,
  onCancel,
  onSpeedChange,
  onMirrorToggle,
  className,
}: VideoRecorderPureProps) {
  const live = phase === 'ready' || phase === 'countdown' || phase === 'recording' || phase === 'paused';
  const capturing = phase === 'recording' || phase === 'paused';
  const hasScript = script.trim().length > 0;
  const showTeleprompter = hasScript && (live || phase === 'starting');
  const progress = Math.min(1, Math.max(0, teleprompterProgress));

  if (phase === 'denied' || phase === 'no_camera' || phase === 'in_use' || phase === 'error') {
    return (
      <CameraProblem
        phase={phase}
        blockedDevices={blockedDevices}
        errorMessage={errorMessage}
        onRetry={onOpenCamera}
        onCancel={onCancel}
        className={className}
      />
    );
  }

  return (
    <div className={cn('flex flex-col items-center gap-3', className)} data-testid="video-recorder">
      {/* The 9:16 frame — the same shape the recipient sees on their phone. */}
      <div
        className="relative aspect-[9/16] w-full max-w-[280px] overflow-hidden rounded-xl bg-stone-900"
        data-testid="video-frame">
        {phase === 'preview' || phase === 'sending' ? (
          previewUrl != null && (
            <video
              src={previewUrl}
              controls
              playsInline
              className="size-full object-cover"
              data-testid="video-preview-player"
            />
          )
        ) : (
          <video
            ref={liveVideoRef}
            autoPlay
            muted
            playsInline
            className={cn('size-full object-cover', mirrored && '-scale-x-100')}
            data-testid="video-live"
          />
        )}

        {phase === 'idle' && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 p-4 text-center">
            <Images.Camera01 className="size-8 text-white/70" />
            <p className="text-sm text-white/80">Record a short video, then send it as a LinkedIn video message.</p>
            <Button size="sm" color="red" onClick={onOpenCamera} data-testid="video-open-camera">
              Turn on camera
            </Button>
          </div>
        )}
        {phase === 'starting' && (
          <div className="absolute inset-0 flex items-center justify-center text-sm text-white/80">
            Waiting for the camera…
          </div>
        )}

        {/* Teleprompter: a band near the top, close to the lens, scrolled by recording time. */}
        {showTeleprompter && (
          <div
            className="absolute inset-x-0 top-0 h-[38%] overflow-hidden bg-black/55 px-3 pt-2"
            data-testid="video-teleprompter">
            <div
              className="text-[19px] font-medium leading-snug text-white"
              style={{ transform: `translateY(calc(${-progress * 100}% + ${progress === 0 ? 0 : 24}px))` }}
              data-testid="video-teleprompter-text"
              data-progress={progress.toFixed(3)}>
              {script}
            </div>
          </div>
        )}

        {phase === 'countdown' && (
          <div
            className="absolute inset-0 flex items-center justify-center text-7xl font-bold text-white drop-shadow"
            data-testid="video-countdown">
            {countdown}
          </div>
        )}

        {(capturing || phase === 'ready') && (
          <div className="absolute bottom-2 left-2 flex items-center gap-1.5 rounded-full bg-black/60 px-2 py-0.5 text-xs font-medium text-white tabular-nums">
            {capturing && (
              <span className={cn('size-2 rounded-full bg-red-500', phase === 'recording' && 'animate-pulse')} />
            )}
            <span data-testid="video-timer">
              {formatClock(elapsedMs)} / {formatClock(maxMs)}
            </span>
          </div>
        )}
      </div>

      {/* Teleprompter controls — only when there's a script to read. */}
      {live && hasScript && (
        <div className="flex w-full max-w-[280px] items-center gap-2">
          <ButtonGroup size="xs" shape="pill" color="gray" tone="outline" equalWidth className="flex-1">
            {SPEEDS.map((s) => (
              <Button
                key={s.value}
                active={speed === s.value}
                onClick={() => onSpeedChange(s.value)}
                tooltip={`Teleprompter speed: ${s.label.toLowerCase()}`}
                data-testid={`video-speed-${s.value}`}>
                {s.label}
              </Button>
            ))}
          </ButtonGroup>
          <Button
            size="xs"
            color="gray"
            tone="outline"
            active={mirrored}
            onClick={onMirrorToggle}
            tooltip={mirrored ? 'Showing a mirror image (the sent video is not mirrored)' : 'Show as a mirror'}
            data-testid="video-mirror"
            icon={<Arrows.FlipBackward className="size-4" />}
          />
        </div>
      )}

      <div className="flex w-full max-w-[280px] gap-2">
        {phase === 'ready' && (
          <>
            <Button size="sm" color="gray" tone="outline" onClick={onCancel}>
              Cancel
            </Button>
            <Button
              size="sm"
              color="red"
              className="flex-1"
              onClick={onRecord}
              data-testid="video-record"
              icon={<Media.Recording01 />}>
              Record
            </Button>
          </>
        )}
        {phase === 'countdown' && (
          <Button size="sm" color="gray" tone="outline" className="flex-1" onClick={onStop}>
            Cancel countdown
          </Button>
        )}
        {capturing && (
          <>
            <Button
              size="sm"
              color="gray"
              tone="outline"
              onClick={onPauseToggle}
              data-testid="video-pause"
              icon={phase === 'paused' ? <Media.Play /> : <Media.PauseCircle />}>
              {phase === 'paused' ? 'Resume' : 'Pause'}
            </Button>
            <Button
              size="sm"
              color="red"
              className="flex-1"
              onClick={onStop}
              data-testid="video-stop"
              icon={<Shapes.Square />}>
              Stop
            </Button>
          </>
        )}
        {(phase === 'preview' || phase === 'sending') && (
          <>
            <Button
              size="sm"
              color="gray"
              tone="outline"
              disabled={phase === 'sending'}
              onClick={onRetake}
              data-testid="video-retake">
              Retake
            </Button>
            <Button
              size="sm"
              color="green"
              className="flex-1"
              disabled={phase === 'sending'}
              onClick={onSend}
              data-testid="video-send"
              icon={<General.Check />}>
              {phase === 'sending' ? 'Sending…' : 'Send video'}
            </Button>
          </>
        )}
      </div>
    </div>
  );
}

/** The steps that unblock a site in Chromium-based browsers, plus the macOS-level switch most people miss. */
function UnblockSteps() {
  return (
    <div className="flex flex-col gap-2 text-left text-xs text-red-700 dark:text-red-300">
      <ol className="list-decimal space-y-0.5 pl-4" data-testid="video-unblock-steps">
        <li>Click the icon just left of the address bar.</li>
        <li>
          Open <span className="font-medium">Site settings</span>, then set{' '}
          <span className="font-medium">Camera</span> and <span className="font-medium">Microphone</span> to{' '}
          <span className="font-medium">Allow</span>.
        </li>
        <li>Reload this page.</li>
      </ol>
      <p data-testid="video-unblock-macos">
        Still blocked on a Mac? <span className="font-medium">System Settings → Privacy &amp; Security → Camera</span>{' '}
        (and <span className="font-medium">Microphone</span>) → turn on your browser.
      </p>
    </div>
  );
}

function CameraProblem({
  phase,
  blockedDevices,
  errorMessage,
  onRetry,
  onCancel,
  className,
}: {
  phase: 'denied' | 'no_camera' | 'in_use' | 'error';
  blockedDevices: BlockedDevices | null;
  errorMessage?: string | null;
  onRetry: () => void;
  onCancel: () => void;
  className?: string;
}) {
  const micOnly = phase === 'denied' && blockedDevices != null && blockedDevices.microphone && !blockedDevices.camera;
  const title =
    phase === 'denied'
      ? micOnly
        ? 'Your browser blocked the microphone for this site'
        : blockedDevices != null && blockedDevices.camera && !blockedDevices.microphone
          ? 'Your browser blocked the camera for this site'
          : 'Your browser blocked the camera and microphone for this site'
      : phase === 'no_camera'
        ? 'No camera found'
        : phase === 'in_use'
          ? 'Your camera is in use by another app'
          : 'Something went wrong with the camera';
  const detail =
    phase === 'denied'
      ? micOnly
        ? 'A video note needs sound — LinkedIn video messages go out with audio — so the microphone has to be allowed too.'
        : null
      : phase === 'no_camera'
        ? 'Connect a camera (or check it isn’t turned off), then try again.'
        : phase === 'in_use'
          ? 'Close Zoom, FaceTime, Teams or another tab using the camera, then try again.'
          : (errorMessage ?? null);
  return (
    <div
      className={cn(
        'flex flex-col items-center gap-3 rounded-lg border border-red-200 bg-red-50 p-4 text-center dark:border-red-800 dark:bg-red-950/40',
        className,
      )}
      data-testid={`video-recorder-${phase}`}>
      <Images.Camera01 className="size-6 text-red-500" />
      <p className="text-sm font-medium text-red-700 dark:text-red-300" data-testid="video-problem-title">
        {title}
      </p>
      {detail != null && <p className="text-xs text-red-700 dark:text-red-300">{detail}</p>}
      {phase === 'denied' && <UnblockSteps />}
      <div className="flex gap-2">
        <Button size="sm" color="red" onClick={onRetry} data-testid="video-retry-camera">
          Try again
        </Button>
        <Button size="sm" color="gray" tone="outline" onClick={onCancel} data-testid="video-problem-cancel">
          Cancel
        </Button>
      </div>
    </div>
  );
}
